import {
  createAsyncThunk,
  ActionReducerMapBuilder,
  createAction,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain, plainToClass } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { RoleService, HTTP_FORBIDDEN_STATUS } from '@vk-hr-tek/core/http';
import { AppError } from '@vk-hr-tek/core/error';
import { ActivityLogService } from '@vk-hr-tek/core/activity-log';
import { Calendar } from '@vk-hr-tek/core/calendar';
import { awaitDispatch } from '@vk-hr-tek/core/redux';
import { Filter } from '@vk-hr-tek/core/filter';
import { History } from '@vk-hr-tek/core/history';
import { QueryService } from '@vk-hr-tek/core/query';
import { showNotification } from '@vk-hr-tek/core/notifications';

import {
  EventListItem as EventListEntity,
  CancelReasonItem as CancelReason,
  EventsService,
  AbsenceListGroup,
  WorkScheduleCalendar,
  Action,
} from '@app/gen/events';
import { AbsenceListResponse } from '@app/gen/absences';
import { UserRoleEnum } from '@app/types';

import { ABSENCES_ATTRIBUTE_IDS } from '../../constants';
import { ThunkExtra, ThunkValuesAndActions } from '../../../app/store';
import { EventsDetailService, EventCacheService } from '../../services';
import {
  GetEventDto,
  CancelEventDto,
  ReturnEventDto,
  RetryEventDto,
  GetAbsencesDto,
  AcceptDto,
} from '../../dto';
import { EventsState, EventsWithRootState } from '../events.state';
import { EventWithValidators, EventsRouter } from '../../types';
import { CandidatesRouter } from '../../../candidates/types';

const EXCLUDED_ACTION_TYPES: Action['type'][] = [
  'system_booking_approve',
  'system_booking_limits_exceeded_approve',
  'system_booking_trip_ordering',
  'system_booking_trip_limit',
  'system_complete_candidate',
  'system_create_request_sw',
  'system_item_voucher_sw',
  'system_trip_info_sw',
  'system_cancel_request_sw',
  'system_business_trip_file_delivery',
  'system_linkedtrip_approveinfo',
  'system_linkedtrip_attrinfo',
  'system_booking_trip_create',
];

const ACTION_TYPES_WITH_LOADING_TEXT: Action['type'][] = [
  'system_validate_sign',
  'system_booking_hook',
  'system_booking_trip_create',
  'system_booking_trip_info',
  'system_booking_trip_change',
  'system_booking_trip_limit_approved',
  'system_booking_trip_cancel',
  'system_business_trip_file_delivery',
  'competency_fill',
];

export const hasOnlyPendingSystemNodes = (
  activeNodes: EventWithValidators['active_nodes'],
) =>
  Array.isArray(activeNodes) &&
  activeNodes.length > 0 &&
  activeNodes.every(
    (node) =>
      (node.action.type.startsWith('system') ||
        node.action.type === 'competency_fill') &&
      !EXCLUDED_ACTION_TYPES.includes(node.action.type),
  );

const allActionsHaveLoadingText = (
  activeNodes: EventWithValidators['active_nodes'],
) =>
  Array.isArray(activeNodes) &&
  activeNodes.length > 0 &&
  activeNodes.every((node) =>
    ACTION_TYPES_WITH_LOADING_TEXT.includes(node.action.type),
  );

const setCancelReasons = createAction<CancelReason[] | null>(
  'events/setCancelReasons',
);

export const setStartEventId = createAction<string | null>(
  'events/setStartEventId',
);

export const setCancelEventDialogState = createAction<boolean>(
  'events/setCancelEventDialogState',
);

const setActiveNodes = createAction<{
  eventId: string;
  activeNodes: EventWithValidators['active_nodes'];
  currentNodes: EventWithValidators['current_nodes'];
}>('events/setActiveNodes');

/** @knipignore */
export const eventView = createAsyncThunk<void, GetEventDto, ThunkExtra>(
  'user/eventView',
  async (getEventDto, { rejectWithValue, extra: { inject } }) => {
    try {
      await inject(ActivityLogService).eventView(getEventDto);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

/** @knipignore */
export const getEventAbsences = createAsyncThunk<
  { groups: AbsenceListGroup[] },
  GetEventDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/getEventAbsences',
  async (getEventDto, { rejectWithValue, extra: { inject } }) => {
    try {
      const eventsService = inject(EventsService);

      const result = await eventsService.getEventEmployeeAbsenceList({
        params: { event_id: getEventDto.id },
      });

      return {
        groups: result.groups,
      };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

const getEventWorkSchedule = createAsyncThunk<
  WorkScheduleCalendar | null,
  { employeeId: string },
  ThunkExtra<EventsWithRootState>
>(
  'events/getEventWorkSchedule',
  async ({ employeeId }, { extra: { inject } }) => {
    try {
      const calendar = inject(Calendar);
      const currentYear = new Date().getFullYear();

      const result = await calendar.downloadWorkSchedule(
        currentYear,
        employeeId,
      );

      return result;
    } catch {
      return null;
    }
  },
);

const autoAccept = createAsyncThunk<
  void,
  ThunkValuesAndActions<AcceptDto>,
  ThunkExtra<EventsWithRootState>
>(
  'events/autoAccept',
  async (
    { values, actions },
    { rejectWithValue, dispatch, getState, extra: { inject } },
  ) => {
    const router = inject<CandidatesRouter>(CandidatesRouter);
    const cache = inject(EventCacheService);

    try {
      const eventDetail = getState().events.detail.data;
      const nodeById = eventDetail?.nodes.find(
        (node) => node.node_id === values.nodeId,
      );

      await inject(EventsDetailService).accept(values);

      if (
        nodeById?.actions.some(
          (action) =>
            action.type === 'accept' &&
            action.extra?.marker === 'candidate_notify',
        )
      ) {
        router.goToList();

        dispatch(showNotification('Анкета создана и отправлена кандидату'));
      } else {
        dispatch(
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          getEvent(
            plainToClass(GetEventDto, { id: values.eventId, blink: true }),
          ),
        );
      }

      cache.deleteValue(values.eventId);
    } catch (err) {
      actions.reject({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEvent = createAsyncThunk<
  EventWithValidators | undefined,
  GetEventDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/getEvent',
  async (
    getEventDto,
    {
      rejectWithValue,
      getState,
      dispatch,
      extra: { inject },
      requestId,
      signal,
    },
  ) => {
    try {
      const eventsService = inject(EventsDetailService);
      const roleService = inject<RoleService>(RoleService);
      const history = inject<History>(History);

      const {
        action: actionFromMail,
        nodeId: nodeIdFromMail,
        signature,
      } = inject(QueryService).parse<{
        action?: string;
        nodeId?: string;
        signature?: string;
      }>(history.location.search);

      const calendar = inject(Calendar);

      calendar.reset();

      const result: EventWithValidators = await eventsService.get(getEventDto);

      dispatch(eventView(getEventDto));

      if (
        roleService.get() === 'employee' &&
        result.nodes.some((node) =>
          node.actions.some((action) =>
            action.form_attributes.some(
              (formAttribute) =>
                formAttribute.type === 'date' &&
                ABSENCES_ATTRIBUTE_IDS.includes(formAttribute.id),
            ),
          ),
        )
      ) {
        await awaitDispatch(dispatch(getEventAbsences(getEventDto)));
      }

      const hasDateFormAttributes = result.nodes.some((node) =>
        node.actions.some((action) =>
          action.form_attributes.some(
            (formAttribute) =>
              formAttribute.type === 'date' ||
              formAttribute.type === 'datetime',
          ),
        ),
      );

      if (hasDateFormAttributes && result.employee?.id) {
        dispatch(getEventWorkSchedule({ employeeId: result.employee.id }));
      }

      if (signal.aborted) {
        return result;
      }

      if (getState().events.detail.currentId !== requestId) {
        return result;
      }

      if (result.nodes && result.nodes.length) {
        const validators = (
          await Promise.all(
            result.nodes.map(async (node) => {
              if (
                node.actions.some((action) => action.form_attributes.length)
              ) {
                return {
                  id: node.node_id,
                  validators: (
                    await eventsService.getNodeValidators({
                      nodeId: node.node_id,
                      eventId: getEventDto.id,
                    })
                  ).validators,
                };
              }

              return null;
            }),
          )
        ).filter((validator) => validator?.validators?.length);

        if (validators.length) {
          result.validators = {};

          validators.forEach((validator) => {
            if (result.validators && validator) {
              result.validators[validator.id] = validator.validators.map(
                (option) => {
                  if (
                    option.type === 'working_day' &&
                    option.from_attribute_id === option.to_attribute_id
                  ) {
                    return {
                      attribute_id: option.from_attribute_id,
                      type: 'single_working_day',
                    };
                  }

                  if (
                    option.type === 'holydays_exclusion' &&
                    option.from_attribute_id === option.to_attribute_id
                  ) {
                    return {
                      attribute_id: option.from_attribute_id,
                      type: 'single_holydays_exclusion',
                    };
                  }

                  return option;
                },
              );
            }
          });
        }
      }

      const attributesDates = result.attributes
        .filter((item) => item.type === 'date')
        .map((attributeDate) =>
          new Date(
            typeof attributeDate?.value === 'string'
              ? attributeDate?.value
              : '',
          ).getFullYear(),
        )
        .sort((startDate, endDate) => startDate - endDate);

      if (attributesDates && attributesDates.length && result.employee?.id) {
        const minYear = attributesDates[0];
        const maxYear = attributesDates[attributesDates.length - 1];
        const yearsRange = Array.from(
          { length: maxYear - minYear + 1 },
          (_, k) => minYear + k,
        );
        await Promise.all(
          yearsRange.map((year) =>
            calendar.downloadWorkSchedule(year, result.employee!.id),
          ),
        );
      }

      if (nodeIdFromMail && actionFromMail === 'accept' && signature) {
        const acceptNode = result.nodes.find(
          (node) =>
            node.actions.some((action) => action.type === 'accept') &&
            node.node_id === nodeIdFromMail,
        );

        if (acceptNode) {
          dispatch(
            autoAccept({
              values: {
                eventId: result.id,
                nodeId: acceptNode.node_id,
                signature,
              },
              actions: { resolve: () => {}, reject: () => {} },
            }),
          );

          return;
        }
      }

      if (nodeIdFromMail && actionFromMail === 'cancel') {
        const acceptNode = result.nodes.find(
          (node) => node.node_id === nodeIdFromMail,
        );

        if (acceptNode) {
          dispatch(setCancelEventDialogState(true));
        }
      }

      return result;
    } catch (err) {
      if (
        getEventDto.shouldRedirect &&
        err instanceof AppError &&
        err.status === HTTP_FORBIDDEN_STATUS
      ) {
        const hasCompanySide = getState().user.data?.has_company_side;
        const router = inject<EventsRouter>(EventsRouter);
        const role = inject<RoleService>(RoleService);

        if (role.get() === 'employee' && hasCompanySide) {
          router.goToDetailOnOtherSide(getEventDto.id);
        }

        if (role.get() === 'company' && !hasCompanySide) {
          router.goToDetailOnOtherSide(getEventDto.id);
        }
      }
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEventActiveNodes = createAsyncThunk<
  void,
  {
    eventId: string;
    activeNodes: EventWithValidators['active_nodes'];
    currentNodes: EventWithValidators['current_nodes'];
  },
  ThunkExtra
>(
  'events/updateEventActiveNodes',
  ({ eventId, activeNodes, currentNodes }, { dispatch }) => {
    if (hasOnlyPendingSystemNodes(activeNodes)) {
      dispatch(setActiveNodes({ eventId, activeNodes, currentNodes }));

      return;
    }

    dispatch(getEvent(plainToClass(GetEventDto, { id: eventId, blink: true })));
  },
);

export const pollEventActiveNodes = createAsyncThunk<
  void,
  { eventId: string },
  ThunkExtra
>(
  'events/pollEventActiveNodes',
  async ({ eventId }, { dispatch, extra: { inject } }) => {
    const event = await inject(EventsDetailService).get({ id: eventId });

    dispatch(
      updateEventActiveNodes({
        eventId,
        activeNodes: event.active_nodes,
        currentNodes: event.current_nodes,
      }),
    );
  },
);

export const retryEvent = createAsyncThunk<
  void,
  {
    values: RetryEventDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<EventsWithRootState>
>(
  'events/retryEvent',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(EventsDetailService).retryEvent(
        plainToClass(RetryEventDto, values),
      );
      actions.resolve(null);
      dispatch(getEvent({ id: values.eventId }));
    } catch (err) {
      actions.resolve({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const cancelEvent = createAsyncThunk<
  void,
  {
    values: CancelEventDto;
    actions: {
      resolve: (value: unknown) => void;
    };
  },
  ThunkExtra<EventsWithRootState>
>(
  'cancelEvent',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      await inject(EventsDetailService).cancelEvent(
        plainToClass(CancelEventDto, values),
      );

      actions.resolve(null);
      dispatch(getEvent({ id: values.eventId }));
    } catch (err) {
      actions.resolve({ [FORM_ERROR]: err });
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const returnEvent = createAsyncThunk<
  void,
  {
    values: ReturnEventDto;
    actions: {
      resolve: (value: unknown) => void;
      reject: (value: unknown) => void;
    };
  },
  ThunkExtra<EventsWithRootState>
>(
  'returnEvent',
  async ({ values, actions }, { rejectWithValue, extra: { inject } }) => {
    try {
      await inject(EventsDetailService).returnEvent(
        plainToClass(ReturnEventDto, values),
      );

      actions.resolve(null);
    } catch (err) {
      actions.reject(null);
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const setResendedNode = createAction<{
  eventId: string;
  nodeId: string;
  timestamp: number;
}>('events/setResendedNode');

const setAbsencesFilters = createAction<Record<string, Filter[]>>(
  'events/setAbsencesFilters',
);

export const getAbsences = createAsyncThunk<
  AbsenceListResponse,
  GetAbsencesDto & {
    role?: UserRoleEnum.Company | UserRoleEnum.Employee;
  },
  ThunkExtra
>(
  'events/getAbsences',
  async (
    getAbsencesListDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    const calendar = inject(Calendar);
    const absencesListService = inject(EventsDetailService);

    const { year, companyId, role } = getAbsencesListDto;
    delete getAbsencesListDto.role;

    try {
      const absences = getState().events.absences;

      let filters = absences.filters;

      if (!filters[companyId]) {
        const availableFilters = (await absencesListService.getAbsenceFilters(
          companyId,
        )) as Filter[];
        filters = { [companyId]: availableFilters };
        dispatch(setAbsencesFilters(filters));
      }

      const [result] = await Promise.all([
        role === UserRoleEnum.Company
          ? absencesListService.getCompanyAbsences(getAbsencesListDto)
          : absencesListService.getEmployeeAbsences(getAbsencesListDto),
        calendar.download(new Date(String(year))),
      ]);

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAdditionalAbsences = createAsyncThunk<
  AbsenceListResponse,
  ThunkValuesAndActions<
    GetAbsencesDto & {
      role?: UserRoleEnum.Company | UserRoleEnum.Employee;
    }
  >,
  ThunkExtra
>(
  'absences/getAdditionalAbsences',
  async (
    { values, actions },
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const absences = getState().events.absences;
      const absencesListService = inject(EventsDetailService);

      let filters = absences.filters;

      if (!filters[values.companyId]) {
        const availableFilters = (await absencesListService.getAbsenceFilters(
          values.companyId,
        )) as Filter[];
        filters = { [values.companyId]: availableFilters };
        dispatch(setAbsencesFilters(filters));
      }

      const result =
        values.role === UserRoleEnum.Company
          ? await absencesListService.getCompanyAbsences(values)
          : await absencesListService.getEmployeeAbsences(values);
      actions.resolve(null);

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const eventDetailReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<EventListEntity, string> & EventsState
  >,
) => {
  builder.addCase(setCancelReasons, (state, action) => {
    state.cancelReasons = action.payload;
  });
  builder.addCase(setStartEventId, (state, action) => {
    state.detail.startEventId = action.payload;
  });
  builder.addCase(setCancelEventDialogState, (state, action) => {
    state.detail.isCancelDialogOpen = action.payload;
  });
  builder.addCase(setActiveNodes, (state, { payload }) => {
    if (!state.detail.data || state.detail.data.id !== payload.eventId) {
      return;
    }

    state.detail.data.active_nodes = payload.activeNodes;
    state.detail.data.current_nodes = payload.currentNodes;

    if (hasOnlyPendingSystemNodes(payload.activeNodes)) {
      if (allActionsHaveLoadingText(payload.activeNodes)) {
        state.detail.status = 'loading-text';
        state.detail.actionTypeForLoadingText = {
          eventId: payload.eventId,
          nodeId: payload.activeNodes[0]?.node_id,
          actionType: payload.activeNodes[0]?.action.type,
          retryText: payload.activeNodes[0]?.action.retry_text,
        };
      } else {
        state.detail.status = 'loading';
      }
    } else {
      state.detail.status = 'complete';
    }
  });
  builder.addCase(getEventAbsences.pending, (state) => {
    state.detail.absences = [];
  });
  builder.addCase(getEventAbsences.fulfilled, (state, { payload }) => {
    state.detail.absences = payload.groups;
  });
  builder.addCase(getEventAbsences.rejected, (state) => {
    state.detail.absences = [];
  });
  builder.addCase(getEventWorkSchedule.pending, (state) => {
    state.detail.workSchedule = null;
  });
  builder.addCase(getEventWorkSchedule.fulfilled, (state, { payload }) => {
    state.detail.workSchedule = payload;
  });
  builder.addCase(getEventWorkSchedule.rejected, (state) => {
    state.detail.workSchedule = null;
  });
  builder.addCase(getEvent.pending, (state, { meta }) => {
    state.detail.currentId = meta.requestId;
    state.detail.status = 'loading';
    state.detail.data = null;
    state.detail.error = null;
  });
  builder.addCase(getEvent.fulfilled, (state, { payload, meta }) => {
    if (payload && meta.requestId === state.detail.currentId) {
      state.detail.data = payload;
      state.detail.error = null;
      state.detail.currentId = null;

      if (hasOnlyPendingSystemNodes(payload.active_nodes)) {
        if (allActionsHaveLoadingText(payload.active_nodes)) {
          state.detail.status = 'loading-text';
          state.detail.actionTypeForLoadingText = {
            eventId: payload.id,
            nodeId: payload.active_nodes[0]?.node_id,
            actionType: payload.active_nodes[0]?.action.type,
            retryText: payload.active_nodes[0]?.action.retry_text,
          };
        } else {
          state.detail.status = 'loading';
        }
      } else {
        state.detail.status = 'complete';
      }
    }
  });
  builder.addCase(getEvent.rejected, (state, { payload, error, meta }) => {
    if (meta.aborted) {
      return;
    }

    if (meta.requestId === state.detail.currentId) {
      state.detail.status = 'failed';
      state.detail.error =
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError);
      state.detail.currentId = null;
    }
  });
  builder.addCase(
    setResendedNode,
    (state, { payload: { eventId, nodeId, timestamp } }) => {
      const exists = state.resendedNodes.find(
        (node) => node.eventId === eventId && node.nodeId === nodeId,
      );

      if (exists) {
        exists.timestamp = timestamp;
      } else {
        state.resendedNodes.push({ eventId, nodeId, timestamp });
      }
    },
  );

  builder.addCase(setAbsencesFilters, (state, action) => {
    state.absences.filters = action.payload;
  });

  builder.addCase(getAbsences.pending, (state) => {
    state.absences.status = 'loading';
  });
  builder.addCase(getAbsences.fulfilled, (state, { payload }) => {
    state.absences.status = 'complete';
    state.absences.items = payload.groups;
    state.absences.intersections = payload.intersections;
    state.absences.total = payload.total;
    state.currentIds = payload.groups.map(({ employee }) => employee.id);

    // absencesAdapter.upsertMany(state, payload.groups);
  });
  builder.addCase(getAbsences.rejected, (state) => {
    state.absences.status = 'failed';
  });

  builder.addCase(getAdditionalAbsences.pending, (state) => {
    state.absences.additionalStatus =
      state.absences.additionalStatus === 'failed'
        ? 'loadingAfterFail'
        : 'loading';
  });
  builder.addCase(getAdditionalAbsences.fulfilled, (state, { payload }) => {
    state.absences.additionalStatus = 'complete';
    state.absences.total = payload.total;
    state.absences.intersections = payload.intersections;
    state.absences.items = [...state.absences.items, ...payload.groups];
    state.currentIds = [
      ...state.currentIds,
      ...payload.groups.map(({ employee }) => employee.id),
    ];

    // absencesAdapter.upsertMany(state, payload.groups);
  });
  builder.addCase(getAdditionalAbsences.rejected, (state) => {
    state.absences.additionalStatus = 'failed';
  });
};
